<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import NearestStation from "@components/NearestStation.vue";
import Dialog from "primevue/dialog";
import ModalBreakdown from "@components/ModalBreakdown.vue";
import UpgradeToFullCourseModal from "@components/UpgradeToFullCourseModal.vue";
import { useBookingStore } from "@stores/BookingStore";
import { useFilterStore } from "@stores/FilterStore";
import {
  formatPrice,
  calculateTotalPrice, // Still used for vaccinations
  calculateFinalPrice,
  calculateClinicFees,
  getConsultationPriceBreakdown,
} from "@app/helpers";
import { cloneDeep } from "lodash-es";

const props = defineProps({
  clinicIndex: Number,
  clinic: {
    type: Object,
    required: true,
  },
  overlay: {
    type: Boolean,
    required: false,
  },
});

const bookingStore = useBookingStore();
const filterStore = useFilterStore();
const router = useRouter();

const nearestStations = computed(() => {
  return props.clinic.nearest_station
    ? props.clinic.nearest_station.split(",")
    : [];
});

// Store the prices/services this clinic offers
const clinicConsultations = ref([]);

// Each clinic will hold it's own data
// Store single/full course options for each vaccination provided by the clinic
// Store consultation prices for each clinic
props.clinic.basket = {
  vaccinations: {
    adult: [],
    child: [],
  },
  consultations: [],
};

// Computed property for patients object
const patients = computed(() => bookingStore.query.patients);
const upgradeModalVisible = ref(false);
const itemiseModalVisible = ref(false);

const viewClinic = () => {
  bookingStore.setClinic(props.clinic);

  // Get the existing query params from the current route
  const currentQuery = router.currentRoute.value.query;

  // Create new query params by merging existing ones with the new clinic_id
  const queryParams = {
    ...currentQuery,
    clinic_id: props.clinic.id,
  };

  // Correctly use the router to navigate with hash mode
  router.replace({
    name: "clinic-profile",
    query: queryParams,
  });
};

onMounted(async () => {
  // Fetch vaccinations when the component is mounted

  if (bookingStore.serviceType === "vaccinations") {
    try {
      const response = await bookingStore.getClinicVaccinations(
        props.clinic.id
      );

      if (response.status === 200) {
        const clinicVaccinations = response.data;
        bookingStore.loadClinicVaccinations(props.clinic, clinicVaccinations); // This will process vaccinations and add to clinic.basket
        bookingStore.clinicVaccinationsReady = true;
        
      }
    } catch (error) {
      console.error("Failed to fetch clinic vaccinations:", error);
    }
  } else {
    try {
      const response = await bookingStore.getClinicConsultationPrices(
        props.clinic.id
      );

      if (response.status === 200) {
        // filter out consultations that have no patient price

        const clinicConsultations = response.data.filter(
          (consultation) => consultation.patient_price > 0
        );

        const consultationDetails = getConsultationPriceBreakdown(
          clinicConsultations,
          bookingStore.query.patients
        );

        props.clinic.finalPrice = consultationDetails.totalPrice;
        props.clinic.totalPrice = consultationDetails.totalPrice;
        props.clinic.basket.consultations = consultationDetails;

      }
    } catch (error) {
      console.error("Failed to fetch clinic consultations:", error);
    }
  }
});

// Calculate the total price when vaccinations are updated.
watch(
  props.clinic.basket.vaccinations,
  () => {
    if (bookingStore.serviceType !== "vaccinations") {
      return;
    }

    props.clinic.finalPrice = calculateFinalPrice(props.clinic);
    props.clinic.totalPrice = calculateTotalPrice(props.clinic);
    props.clinic.totalFees = calculateClinicFees(props.clinic.totalPrice);
  },
  { deep: true, immediate: true }
);

// Watch for global course preference changes and apply them to this clinic
watch(
  () => bookingStore.query.coursePreferences,
  (coursePreferences) => {
    if (bookingStore.serviceType !== "vaccinations" || !props.clinic.basket?.vaccinations) {
      return;
    }

    // Apply course preferences to adult vaccinations
    props.clinic.basket.vaccinations.adult.forEach((patientVaccinations) => {
      patientVaccinations.forEach((vaccination) => {
        if (coursePreferences[vaccination.id] === 'full') {
          vaccination.course = 'full';
          // Update pricing for full course
          const dose = bookingStore.vaccinations.find(v => v.id == vaccination.id)?.dose || 1;
          vaccination.finalPrice = vaccination.priceBeforeFees * dose;
        } else {
          vaccination.course = 'single';
          vaccination.finalPrice = vaccination.price;
        }
      });
    });

    // Apply course preferences to child vaccinations
    props.clinic.basket.vaccinations.child.forEach((patientVaccinations) => {
      patientVaccinations.forEach((vaccination) => {
        if (coursePreferences[vaccination.id] === 'full') {
          vaccination.course = 'full';
          // Update pricing for full course
          const dose = bookingStore.vaccinations.find(v => v.id == vaccination.id)?.dose || 1;
          vaccination.finalPrice = vaccination.priceBeforeFees * dose;
        } else {
          vaccination.course = 'single';
          vaccination.finalPrice = vaccination.price;
        }
      });
    });

    // Recalculate clinic totals
    props.clinic.finalPrice = calculateFinalPrice(props.clinic);
    props.clinic.totalPrice = calculateTotalPrice(props.clinic);
    props.clinic.totalFees = calculateClinicFees(props.clinic.totalPrice);
  },
  { deep: true }
);
</script>

<template>
  <article class="@container">
    <div
      class="@4xl:grid @4xl:grid-cols-12 bg-white px-20 md:px-30 py-25 border border-grey-border rounded-2xl">
      <header class="col-span-9">
        <div class="flex justify-between gap-4">
          <h2 class="-mt-[4px] mb-30 font-medium text-18 xs:text-20 grow-1">
            {{ clinic.pharmacy_name }}
          </h2>
          <div class="@4xl:hidden mb-40 text-right shrink-0">
            <span class="block font-medium text-20 xs:text-32">
              £{{ formatPrice(clinic.finalPrice) }}
            </span>
            <span class="xs:hidden block mb-5 max-w-[120px] text-13">
              For a Private Vaccination
            </span>
            <button class="font-medium text-blue">View Breakdown</button>
          </div>
        </div>

        <ul class="flex flex-col gap-3 text-grey">
          <li class="flex gap-4">
            <i class="-mt-[4px] text-20 text-grey/[0.5] ri-map-pin-2-fill"></i>
            <ul class="md:flex gap-4 gap-y-1 md:gap-x-8 grid xs:grid-cols-2">
              <li class="">{{ Number(clinic.distance).toFixed(1) }} miles</li>
              <li class="">{{ clinic.address_1 }}</li>
            </ul>
          </li>
          <li class="flex gap-4">
            <i class="-mt-[4px] text-20 text-grey/[0.5] ri-first-aid-kit-line">
            </i>
            <ul
              class="md:flex gap-x-4 gap-y-1 md:gap-x-6 grid sm:grid-cols-2 list-check">
              <li v-if="Number(clinic.has_male)">Male clinicians</li>
              <li v-if="Number(clinic.has_female)">Female clinicians</li>
            </ul>
          </li>
          <li class="flex gap-4">
            <i class="-mt-[4px] text-20 text-grey/[0.5] ri-car-line"></i>
            <ul
              class="md:flex gap-x-4 gap-y-1 md:gap-x-6 grid sm:grid-cols-2 list-check">
              <li v-if="Number(clinic.private_parking)">Private parking</li>
              <li v-if="Number(clinic.off_street_parking)">Off-street</li>
              <li v-if="Number(clinic.pay_display)">Pay and display</li>
              <li v-if="Number(clinic.disability_access)">Disability Access</li>
            </ul>
          </li>

          <li class="hidden lg:flex gap-4 mt-20">
            <ul class="md:flex gap-x-4 gap-y-1 md:gap-x-6 grid xs:grid-cols-2">
              <li v-for="(station, index) in nearestStations" :key="index">
                <NearestStation :name="station" />
              </li>
            </ul>
          </li>
        </ul>
      </header>

      <footer
        class="flex flex-col @4xl:justify-between @4xl:items-end col-span-3">
        <div class="hidden @4xl:block mb-40 text-right">
          <span class="block font-medium text-32">
            £{{ formatPrice(clinic.finalPrice) }}
          </span>

          <p class="mb-10" v-if="bookingStore.serviceType === 'consultations'">
            For a Travel Consultation
          </p>

          <button
            @click="itemiseModalVisible = true"
            class="font-medium text-blue">
            View Breakdown
          </button>
          <Dialog
            v-model:visible="itemiseModalVisible"
            modal
            header="View Breakdown"
            class="w-full max-w-[550px] max-h-[650px]">
            <div class="p-20 rounded-xl max-h-[500px] overflow-y-auto">
              <p class="mb-40">View how your price is being broken down.</p>

              <template v-if="bookingStore.serviceType === 'vaccinations'">
                <ul class="flex flex-col gap-4">
                  <li
                    v-for="(vaccinations, adultIndex) in clinic.basket
                      .vaccinations.adult"
                    :key="`breakdown-adult-${adultIndex}`">
                    <ModalBreakdown
                      type="adult"
                      :patient="adultIndex"
                      :vaccinations="vaccinations"
                      :clinic="clinic"
                      :opened="index === 0" />
                  </li>
                  <li
                    v-for="(vaccinations, childIndex) in clinic.basket
                      .vaccinations.child"
                    :key="`breakdown-child-${childIndex}`">
                    <ModalBreakdown
                      type="child"
                      :patient="childIndex"
                      :vaccinations="vaccinations"
                      :clinic="clinic" />
                    <!-- opened will default to false -->
                  </li>
                </ul>
              </template>

              <template
                v-else-if="bookingStore.serviceType === 'consultations'">
                <ModalBreakdown
                  type="consultation_summary"
                  :clinic="clinic"
                  serviceType="consultations"
                  :consultationDetails="clinic.basket.consultations"
                  :opened="true" />
              </template>
            </div>
            <template #footer>
              <p class="font-medium text-20">
                Total £{{ formatPrice(clinic.finalPrice) }}
              </p>
            </template>
          </Dialog>
        </div>
        <div class="flex flex-col @4xl:items-end gap-4 mt-40 @4xl:mt-0">
          <button
            v-if="bookingStore.serviceType === 'vaccinations'"
            class="inline-block font-medium text-blue"
            @click="upgradeModalVisible = true">
            Upgrade to Full Course
          </button>

          <UpgradeToFullCourseModal
            v-model:visible="upgradeModalVisible"
            :clinic="clinic"
            clinicNameField="pharmacy_name" />

          <Btn
            class="px-40 w-full font-medium"
            outline
            blue
            @click="viewClinic">
            Book
          </Btn>
        </div>
      </footer>
    </div>
  </article>
</template>

<style scoped lang="scss"></style>
